import { useState } from 'react'
import {
  ContentTooltip,
  HeadItemType,
  Icon,
  Pagination,
  SlimButton,
  Table,
  Tooltip,
  Typography
} from 'components'
import { useDispatch, useSelector } from 'store/store'
import { setMaterialList } from 'store/ExtraVolumeContext/ExtraVolumeSlice'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import {
  Cor,
  ExtraMaterial,
  MaterialItensAction,
  TipoCriticaVolume
} from 'store/ExtraVolumeContext/Types/ExtraVolumeType'
import { toAllFirstLetterUpper } from 'commons/utils/string'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { ActionPerm } from 'components/Structures/Header/constants'
import { usePermissions } from 'commons/customHooks/useAuth'
import { ModalConfirmation } from '../ModalConfirmation'
import { CriticismType, TypesValidations, YesNoNotApplicable } from 'constants/enums'
import { formatDate } from 'commons/utils/date'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'
import { OrderBlockers } from 'constants/enums'

const LIMIT_AMOUNT = 100
const MIN_STOCK_AMOUNT = 0

type VolumeTableProps = {
  payload: ExtraMaterial[]
  handlePageChange: (page: number) => void
  updateTable: () => void
}
interface ActionItems extends MaterialItensAction {
  cores: string[]
}
const VolumeTable = ({ payload, handlePageChange, updateTable }: VolumeTableProps) => {
  const [showModal, setShowModal] = useState(false)
  const [isReject, setIsReject] = useState(false)
  const [critic, setCritic] = useState<TipoCriticaVolume>('')
  const [actionItems, setActionItems] = useState<MaterialItensAction[] | ActionItems[]>(
    []
  )

  const { pagination, materialsList } = useSelector((store) => store.extraVolume)
  const dispatch = useDispatch()
  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.EXTRA_VOLUME, action)
  }

  const handleConfirmation = (isReject: boolean, item?: ExtraMaterial) => {
    if (item) {
      const actionItens = isReject
        ? { nr_pedi_rasc: item.NumeroPedido, nr_item_pedi_rasc: item.NumeroItem }
        : {
            nr_pedi_rasc: item.NumeroPedido,
            nr_item_pedi_rasc: item.NumeroItem,
            cores: item.Cores.map((c) => c.Codigo)
          }
      setCritic(item.TipoCritica)
      setActionItems([actionItens])
    } else {
      const checked = materialsList.filter((m) => m.checked)
      const critic = checked.some((c) => c.TipoCritica === CriticismType.QUOTA_STOCK)
        ? CriticismType.QUOTA_STOCK
        : checked.some((c) => c.TipoCritica === CriticismType.STOCK)
        ? CriticismType.STOCK
        : CriticismType.QUOTA
      setCritic(critic)
      const arrItens = isReject
        ? checked.map((i) => ({
            nr_pedi_rasc: i.NumeroPedido,
            nr_item_pedi_rasc: i.NumeroItem
          }))
        : checked.map((i) => ({
            nr_pedi_rasc: i.NumeroPedido,
            nr_item_pedi_rasc: i.NumeroItem,
            cores: i.Cores.map((c) => c.Codigo)
          }))
      setActionItems(arrItens)
    }
    setIsReject(isReject)
    setShowModal(true)
  }
  const handleConfirmationAction = () => {
    setShowModal(false)
    updateTable()
  }

  const getCheckedExtraVolume = () => {
    return materialsList.filter((item) => item.checked)
  }

  const BlockersTable = ({ item }: { item: ExtraMaterial }) => {
    const orderBlockers = [
      OrderBlockers.ZONE,
      OrderBlockers.GRV,
      OrderBlockers.GNV
    ] as string[]

    const blockers = [...item.Bloqueios].sort(
      (a, b) => orderBlockers.indexOf(a.Tipo) - orderBlockers.indexOf(b.Tipo)
    )

    const headItems = blockers.map((blocker) => {
      return { label: blocker.Tipo, columnName: blocker.Tipo }
    })

    const blockersHead: HeadItemType[] = [
      { label: 'Bloqueio', columnName: 'bloqueio' },
      ...headItems
    ]

    const blockersBody = [
      {
        id: `bloq-cotas-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`,
        content: [
          <S.ColContent
            key={`label-bloq-cotas-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            <S.RowText type={'neutral'}>Cota digitada</S.RowText>
            <S.RowText small type={'neutral'}>
              Saldo da cota
            </S.RowText>
          </S.ColContent>,
          ...blockers.map((b) => (
            <S.ColContent
              key={`${b.Tipo}-bloq-cotas-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            >
              <S.RowText type={b.Porcentagem >= LIMIT_AMOUNT ? 'negative' : 'neutral'}>
                {formatToPTBRNumber(b.Porcentagem, false)}%
              </S.RowText>
              <S.RowText
                type={b.Porcentagem >= LIMIT_AMOUNT ? 'negative' : 'neutral'}
                small
              >
                {numberToDecimalString(b.SaldoCota)}
              </S.RowText>
            </S.ColContent>
          ))
        ]
      }
    ]

    return (
      <ContentTooltip
        key={`cota-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
        title={
          <span>
            <strong>{item.CodigoMaterial}</strong> • {item.DescricaoMateial}
          </span>
        }
        content={<Table headData={blockersHead} bodyData={blockersBody} rowHeight={48} />}
      >
        <S.RowText type={item.CotaDigitada >= LIMIT_AMOUNT ? 'negative' : 'neutral'}>
          {formatToPTBRNumber(item.CotaDigitada, false)}%
        </S.RowText>
      </ContentTooltip>
    )
  }

  const ColorSizesTable = ({ item, cor }: { item: ExtraMaterial; cor: Cor }) => {
    const headItems = [
      { label: 'Numeração', columnName: 'numeracao' },
      ...cor.Tamanhos.map((t) => {
        return { label: t.Numeracao, columnName: t.Numeracao }
      })
    ]

    const tableBody = [
      {
        id: `tam-cor-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`,
        content: [
          <S.ColContent
            key={`Label-vol-vol-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            <S.RowText type={'neutral'}>Volume</S.RowText>
            <S.RowText small type={'neutral'}>
              Estoque
            </S.RowText>
          </S.ColContent>,
          ...cor.Tamanhos.map((t) => (
            <S.ColContent
              key={`${t.Numeracao}-vol-vol-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            >
              <S.RowText type={'neutral'}>
                {numberToDecimalString(t.VolumeSocilitado)}
              </S.RowText>
              <S.RowText
                type={t.QuantidadeEstoque <= MIN_STOCK_AMOUNT ? 'negative' : 'neutral'}
                small
              >
                {numberToDecimalString(t.QuantidadeEstoque)}
              </S.RowText>
            </S.ColContent>
          ))
        ]
      }
    ]

    return (
      <ContentTooltip
        key={`vol-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
        title={
          <S.TitleRow>
            <span>
              <strong>{item.CodigoMaterial}</strong> • {item.DescricaoMateial}
            </span>{' '}
            <span>
              <strong>Regra de exceção</strong>: {item.RegraExcecao}
            </span>
          </S.TitleRow>
        }
        content={<Table headData={headItems} bodyData={tableBody} rowHeight={48} />}
      >
        <S.RowText type={'neutral'}>
          {numberToDecimalString(cor.VolumeSolicitado)}
        </S.RowText>
      </ContentTooltip>
    )
  }

  const tableHead: HeadItemType[] = [
    { label: '', columnName: '' },
    { label: 'Pedido', columnName: 'Pedido' },
    { label: 'Material', columnName: 'Material' },
    { label: 'Vol. solicitado', columnName: 'VolumeSolicitado' },
    { label: 'Mês cota', columnName: 'MesCota' },
    { label: 'Bloqueio', columnName: 'Bloqueio' },
    { label: '% de cota digitada', columnName: 'PorcentagemCotaDigitada' },
    { label: 'Saldo da cota', columnName: 'SaldoCota' },
    { label: 'Estoque', columnName: 'Estoque' },
    { label: 'Regra de exceção', columnName: 'RegraExcecao' },
    { label: 'Tecnologia', columnName: 'Tecnologia' },
    { label: 'S/ Cluster', columnName: 'SemCluster' },
    { label: 'Escritório', columnName: 'EscritorioVenda' },
    { label: 'Executivo', columnName: 'Executivo' },
    { label: 'Cliente', columnName: 'Cliente' },
    { label: 'Data de solicitação', columnName: 'DataSolicitacao' },
    { label: 'Ações', columnName: 'Acoes' }
  ]

  const setChecked = (checked: boolean, id?: string) => {
    if (id) {
      const newMaterialsList = materialsList.map((item) => {
        if (
          `${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}` ===
          id
        ) {
          return { ...item, checked }
        }
        return item
      })
      dispatch(setMaterialList(newMaterialsList))
    } else {
      const newMaterialsList = materialsList.map((item) => {
        return { ...item, checked }
      })
      dispatch(setMaterialList(newMaterialsList))
    }
  }

  const setExpanded = (id: string) => {
    const newMaterialsList = materialsList.map((item) => {
      if (
        `${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}` ===
        id
      ) {
        return { ...item, expanded: !item?.expanded }
      }
      return item
    })
    dispatch(setMaterialList(newMaterialsList))
  }

  const getMaterialsData = () => {
    const tableData = []
    const emptyTd = new Array(tableHead.length - 10).fill('')

    const materialsData = payload.map((item) => {
      return {
        id: `${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`,
        checked: item.checked ?? false,
        expanded: item.expanded ?? false,
        onDoubleClick: () => {
          return
        },
        content: [
          <S.RowIcon
            key={`icons-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {' '}
            {item.TipoCritica !== CriticismType.STOCK && (
              <Tooltip title={'Indisponível por cota'} placement="top" arrow>
                <span>
                  <Icon icon="report" color={theme.colors.alert.warning.dark} size="15" />
                </span>
              </Tooltip>
            )}
            {item.TipoCritica !== CriticismType.QUOTA && (
              <Tooltip title={'Indisponível por estoque'} placement="top" arrow>
                <span>
                  <Icon icon="box" color={theme.colors.alert.warning.dark} size="15" />
                </span>
              </Tooltip>
            )}
          </S.RowIcon>,
          <S.RowText
            type={'neutral'}
            key={`ped-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {item.NumeroPedido}
          </S.RowText>,
          <S.ColContent
            key={`cod-material-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            <S.RowIcon>
              <S.RowText type={'neutral'}>{item.CodigoMaterial}</S.RowText>
              {item.TemRestricaoAnalise === TypesValidations.YES && (
                <Tooltip
                  title={'Produto recentemente reprovado com restrição'}
                  placement="top"
                  arrow
                >
                  <span>
                    <Icon
                      icon="sync-problem"
                      color={theme.colors.alert.danger[40]}
                      size="15"
                    />
                  </span>
                </Tooltip>
              )}
            </S.RowIcon>
            <Typography design="hav-default-04-text">
              {toAllFirstLetterUpper(item.DescricaoMateial)}
            </Typography>
          </S.ColContent>,
          <S.RowText
            key={`vol-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {numberToDecimalString(item.VolumeSolicitado)}
          </S.RowText>,
          <S.RowText
            type={'neutral'}
            key={`mes-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {item.MesCota}
          </S.RowText>,
          <S.RowText
            type={'neutral'}
            key={`bloq-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {item.TipoBLoqueio}
          </S.RowText>,
          <BlockersTable
            item={item}
            key={`cota-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          />,
          <S.RowText
            type={'neutral'}
            key={`sal-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {numberToDecimalString(item.SaldoCota)}
          </S.RowText>,
          <S.RowText
            type={'neutral'}
            key={`est-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {numberToDecimalString(item.VolumeEstoque)}
          </S.RowText>,
          <S.RowText
            type={'neutral'}
            key={`reg-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {item.RegraExcecao}
          </S.RowText>,
          <Tooltip
            key={`tec-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            title={`Produção ≈ ${item.TempoProducaoTecnologia} dias`}
            placement="top"
            arrow
          >
            <S.RowText type={'neutral'}>{item.Tecnologia}</S.RowText>
          </Tooltip>,
          <S.RowText
            key={`clu-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {item.TemCluster.toLocaleUpperCase() === YesNoNotApplicable.YES_UPPERCASE
              ? YesNoNotApplicable.YES
              : YesNoNotApplicable.NO}
          </S.RowText>,
          <S.RowText
            type={'neutral'}
            key={`exe-${item.EscritorioVenda}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {item.EscritorioVenda}
          </S.RowText>,
          <S.RowText
            type={'neutral'}
            key={`exe-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {item.NomeExecutivo}
          </S.RowText>,
          <S.RowText
            type={'neutral'}
            key={`clie-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {item.CodigoCliente}
          </S.RowText>,
          <S.RowText
            type={'neutral'}
            key={`dat-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {formatDate(item.DataSolicitacao, 'DD/MM')}
          </S.RowText>,
          <S.ActionsCell
            key={`action-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            <Tooltip
              title={shouldDisable(ACTIONS_SCREENS.APPROVE) ? '' : 'Aprovar Negociação'}
              placement="top"
              arrow
            >
              <S.ApproveButton
                disabled={shouldDisable(ACTIONS_SCREENS.APPROVE)}
                onClick={() => handleConfirmation(false, item)}
              >
                <Icon icon="task-alt" color={theme.colors.neutral.white} size="15" />
              </S.ApproveButton>
            </Tooltip>

            <Tooltip
              title={shouldDisable(ACTIONS_SCREENS.DISMISS) ? '' : 'Reprovar Negociação'}
              placement="top"
              arrow
            >
              <S.RejectButton
                disabled={shouldDisable(ACTIONS_SCREENS.DISMISS)}
                onClick={() => handleConfirmation(true, item)}
              >
                <Icon icon="cancel" color={theme.colors.neutral.white} size="15" />
              </S.RejectButton>
            </Tooltip>
          </S.ActionsCell>
        ],
        subRows: item.Cores?.length
          ? item.Cores.map((cor) => {
              return {
                id: `${cor.Codigo}-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`,
                content: [
                  <S.RowText
                    type={'neutral'}
                    key={`label-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
                  >
                    <span id="colorText">Cor</span>
                  </S.RowText>,
                  <S.RowText
                    type={'neutral'}
                    key={`cod-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
                  >
                    {cor.Codigo}
                  </S.RowText>,
                  <S.RowText
                    type={'neutral'}
                    key={`des-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
                  >
                    {cor.Descricao}
                  </S.RowText>,
                  <ColorSizesTable
                    item={item}
                    cor={cor}
                    key={`vol-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
                  />,
                  '',
                  '',
                  '',
                  '',
                  <S.RowText
                    key={`est-q-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
                    type={'neutral'}
                  >
                    {numberToDecimalString(cor.QuantidadeEstoque)}
                  </S.RowText>,
                  ...emptyTd
                ]
              }
            })
          : undefined
      }
    })

    tableData.push(...materialsData)

    return tableData
  }

  const VolumeTableFooter = () => {
    return (
      <S.FooterDiv>
        <S.FooterRow>
          <Pagination
            numberOfPages={pagination.totalPages}
            currentPage={pagination.currentPage}
            setCurrentPage={handlePageChange}
            numberOfItems={pagination.totalItems}
            itemsPerPage={pagination.pageSize}
          />
          <S.FooterActions>
            <Tooltip
              placement="top"
              arrow
              title={
                !getCheckedExtraVolume().length ? (
                  <span>
                    Selecione uma ou mais linhas na lista <br />
                    para executar ações em massa
                  </span>
                ) : (
                  ''
                )
              }
            >
              <div>
                <SlimButton
                  variant="danger"
                  content="Reprovar"
                  icon={<Icon icon="cancel" size="15" />}
                  onClick={() => {
                    handleConfirmation(true)
                  }}
                  disabled={
                    shouldDisable(ACTIONS_SCREENS.DISMISS) ||
                    !getCheckedExtraVolume().length
                  }
                />
              </div>
            </Tooltip>
            <Tooltip
              placement="top"
              arrow
              title={
                !getCheckedExtraVolume().length ? (
                  <span>
                    Selecione uma ou mais linhas na lista <br />
                    para executar ações em massa
                  </span>
                ) : (
                  ''
                )
              }
            >
              <div>
                <SlimButton
                  variant="success"
                  content="Aprovar"
                  icon={<Icon icon="task-alt" size="15" />}
                  onClick={() => handleConfirmation(false)}
                  disabled={
                    shouldDisable(ACTIONS_SCREENS.APPROVE) ||
                    !getCheckedExtraVolume().length
                  }
                />
              </div>
            </Tooltip>
          </S.FooterActions>
        </S.FooterRow>
      </S.FooterDiv>
    )
  }

  return (
    <>
      <Table
        rowHeight={40}
        headData={tableHead}
        bodyData={getMaterialsData()}
        iconColumns={[0]}
        actionColumns={[16]}
        setChecked={setChecked}
        toggleExpand={setExpanded}
        hasCheckbox
        checkboxSize="small"
      />
      {getMaterialsData().length > 0 && <VolumeTableFooter />}
      {showModal && (
        <ModalConfirmation
          isOpen={showModal}
          setIsOpen={setShowModal}
          criticType={critic}
          itens={actionItems}
          isReject={isReject}
          handleContinue={handleConfirmationAction}
          handleReturnAction={() => setShowModal(false)}
        />
      )}
    </>
  )
}

export default VolumeTable
